import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Heading, Text, Flex, Spinner, SimpleGrid, Divider,
  Button, HStack, Input, Center, Container, VStack, InputGroup, 
  InputLeftElement, Badge, Icon, ScaleFade
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { IoSearchOutline, IoCalendarOutline } from "react-icons/io5";
import ApiResponse from "../../services/ApiResponse";
import { Evento } from "../../services/models/Evento";
import UnauthorizedPage from "../UnauthorizedPage";
import ServerErrorPage from "../ServerErrorPage";
import EventoCard from "../../components/Eventos/EventoCard";
import EventoPrincipal from "../../components/Eventos/EventoPrincipal";

const ENDPOINT = "eventos";
const ITEMS_PER_PAGE = 8;
const ACCENT_COLOR = "#E9681F"; // Naranja institucional

const normalizeText = (text: string): string =>
  text?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() || "";

const EventosPage = () => {
  const [response, setResponse] = useState(new ApiResponse<Evento[]>());
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchEventos = async () => {
      const res = new ApiResponse<Evento[]>();
      await res.useFetch(ENDPOINT, "GET");
      setResponse(res);
    };
    fetchEventos();
  }, []);

  // Estilo Glassmorphism Pro
  const glassStyle = {
    bg: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(15px)",
    border: "1px solid",
    borderColor: "whiteAlpha.200",
    borderRadius: "2xl",
    transition: "all 0.3s ease",
  };

  // Lógica de datos centralizada (Filtrado + Paginación)
  const { paginatedItems, totalPages, principalEvent, hasResults } = useMemo(() => {
    const data = response.data || [];
    const term = normalizeText(searchTerm);
    
    const filtered = data
      .filter((e) => normalizeText(e.titulo).includes(term))
      .sort((a, b) => b.id - a.id);

    const principal = filtered[0] || null;
    const others = filtered.slice(1); 
    
    const total = Math.ceil(others.length / ITEMS_PER_PAGE);
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginated = others.slice(start, start + ITEMS_PER_PAGE);

    return { 
      paginatedItems: paginated, 
      totalPages: total, 
      principalEvent: principal,
      hasResults: filtered.length > 0
    };
  }, [response.data, searchTerm, currentPage]);

  if (response.loading) return (
    <Flex justify="center" align="center" height="100vh" bg="#0a192f">
      <VStack spacing={4}>
        <Spinner size="xl" thickness="4px" speed="0.65s" color={ACCENT_COLOR} />
        <Text color="whiteAlpha.600">Cargando agenda cultural...</Text>
      </VStack>
    </Flex>
  );

  if (response.responseCode === 401) return <UnauthorizedPage />;
  if (response.responseCode !== 200 && response.responseCode !== 0) return <ServerErrorPage />;

  return (
    <Box bgGradient="linear(to-b, #0f172a, #0a192f)" minH="100vh" pb={20}>
      <Container maxW="container.xl" pt={16}>
        
        {/* Header - Identidad Visual */}
        <VStack spacing={4} textAlign="center" mb={16}>
          <ScaleFade initialScale={0.9} in={true}>
            <Badge 
              variant="outline" 
              colorScheme="orange" 
              px={4} py={1} 
              borderRadius="full" 
              letterSpacing="widest"
              mb={4}
            >
              Goya · Corrientes
            </Badge>
            <Heading 
              as="h1" 
              size="2xl" 
              color="white" 
              fontWeight="900" 
              letterSpacing="tight"
            >
              Agenda de <Box as="span" color={ACCENT_COLOR}>Eventos</Box>
            </Heading>
            <Text color="whiteAlpha.700" fontSize="lg" maxW="2xl" mx="auto" mt={4}>
              Explorá las actividades más destacadas de nuestra ciudad.
            </Text>
          </ScaleFade>
        </VStack>

        {!hasResults ? (
          <Center py={24} {...glassStyle}>
            <VStack spacing={4}>
              <Icon as={IoSearchOutline} boxSize={12} color="whiteAlpha.300" />
              <Text color="whiteAlpha.800" fontSize="xl">
                No hay resultados para "{searchTerm}"
              </Text>
              <Button 
                variant="link" 
                color={ACCENT_COLOR} 
                onClick={() => setSearchTerm("")}
              >
                Ver todos los eventos
              </Button>
            </VStack>
          </Center>
        ) : (
          <VStack spacing={16} align="stretch">
            
            {/* Evento Héroe (Principal) */}
            {principalEvent && (
              <Box transform="scale(1)" transition="0.3s" _hover={{ transform: "scale(1.005)" }}>
                <EventoPrincipal evento={principalEvent} />
              </Box>
            )}

            <Divider borderColor="whiteAlpha.200" />

            {/* Barra de Filtros */}
            <Flex 
              direction={{ base: "column", md: "row" }} 
              justify="space-between" 
              align={{ md: "center" }} 
              gap={8}
            >
              <HStack spacing={4}>
                <Center p={3} bg="whiteAlpha.100" borderRadius="xl">
                   <Icon as={IoCalendarOutline} color={ACCENT_COLOR} boxSize={6} />
                </Center>
                <Heading size="lg" color="white">Próximos eventos</Heading>
              </HStack>
              
              <InputGroup maxW={{ base: "100%", md: "400px" }} size="lg">
                <InputLeftElement pointerEvents="none">
                  <IoSearchOutline color="white" opacity={0.4} />
                </InputLeftElement>
                <Input
                  {...glassStyle}
                  placeholder="Buscar por título..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  color="white"
                  _placeholder={{ color: "whiteAlpha.400" }}
                  _focus={{ borderColor: ACCENT_COLOR, boxShadow: `0 0 0 1px ${ACCENT_COLOR}` }}
                />
              </InputGroup>
            </Flex>

            {/* Grilla de Eventos */}
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={10}>
              {paginatedItems.map((evento, index) => (
                <ScaleFade key={evento.id} in={true} delay={index * 0.05}>
                  <Box 
                    transition="all 0.3s" 
                    _hover={{ transform: "translateY(-10px)" }}
                  >
                    <EventoCard evento={evento} />
                  </Box>
                </ScaleFade>
              ))}
            </SimpleGrid>

            {/* Paginación Pro */}
            {totalPages > 1 && (
              <Flex justify="center" pt={12}>
                <HStack spacing={3} p={2} bg="whiteAlpha.50" borderRadius="2xl">
                  <Button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    isDisabled={currentPage === 1}
                    variant="ghost"
                    color="white"
                    _hover={{ bg: "whiteAlpha.200" }}
                  >
                    <ChevronLeftIcon boxSize={6} />
                  </Button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      size="sm"
                      bg={currentPage === page ? ACCENT_COLOR : "transparent"}
                      color="white"
                      _hover={{ bg: currentPage === page ? ACCENT_COLOR : "whiteAlpha.200" }}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  ))}

                  <Button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    isDisabled={currentPage === totalPages}
                    variant="ghost"
                    color="white"
                    _hover={{ bg: "whiteAlpha.200" }}
                  >
                    <ChevronRightIcon boxSize={6} />
                  </Button>
                </HStack>
              </Flex>
            )}
          </VStack>
        )}
      </Container>
    </Box>
  );
};

export default EventosPage;